import moment from 'moment';

export const FILTER_TYPES = {
    RANGO: 'rango',
    MAYOR: 'mayor',
    MES: 'mes',
    ANNO: 'anno',
};

export const YEAR_LOOKBACK = 15;

export function getMinYear(now = moment()) {
    return now.clone().subtract(YEAR_LOOKBACK, 'years').year();
}

export function getMaxYear(now = moment()) {
    return now.year();
}

function calcularRango(fechaDesde, fechaHasta) {
    return {
        startDate: moment(fechaDesde).startOf('day').valueOf(),
        endDate: moment(fechaHasta).startOf('day').valueOf(),
    };
}

/**
 * Maps dashboard date filter options to startDate/endDate timestamps.
 * Mirrors safite CalcularFechas() for: rango, mayor, mes, anno.
 */
export default function calcularFechas({
    filterType,
    fechaDesde,
    fechaHasta,
    fechaMayor,
    mes,
    annoMes,
    anno,
}) {
    const today = moment().startOf('day');

    switch (filterType) {
        case FILTER_TYPES.RANGO:
            return calcularRango(fechaDesde, fechaHasta);
        case FILTER_TYPES.MAYOR:
            return {
                startDate: moment(fechaMayor).startOf('day').valueOf(),
                endDate: today.valueOf(),
            };
        case FILTER_TYPES.MES: {
            const start = moment({ year: annoMes, month: mes - 1, day: 1 }).startOf('day');
            const end = start.clone().endOf('month').startOf('day');
            return {
                startDate: start.valueOf(),
                endDate: end.valueOf(),
            };
        }
        case FILTER_TYPES.ANNO: {
            const start = moment({ year: anno, month: 0, day: 1 }).startOf('day');
            const end = moment({ year: anno, month: 11, day: 31 }).startOf('day');
            return {
                startDate: start.valueOf(),
                endDate: end.valueOf(),
            };
        }
        default:
            return calcularRango(fechaDesde, fechaHasta);
    }
}

export function normalizeLegacyFilters(filters) {
    if (filters.filterType === 'igual') {
        return {
            ...filters,
            filterType: FILTER_TYPES.RANGO,
            fechaDesde: filters.fechaIgual ?? filters.fechaDesde,
            fechaHasta: filters.fechaIgual ?? filters.fechaHasta,
        };
    }

    return filters;
}

function isValidTimestamp(value) {
    return value != null && moment(value).isValid();
}

function isYearInRange(year, now = moment()) {
    return Number.isFinite(year) && year >= getMinYear(now) && year <= getMaxYear(now);
}

export function isValidFilterDraft(draft, now = moment()) {
    const { filterType } = draft;

    switch (filterType) {
        case FILTER_TYPES.RANGO:
            return isValidTimestamp(draft.fechaDesde) && isValidTimestamp(draft.fechaHasta);
        case FILTER_TYPES.MAYOR:
            return isValidTimestamp(draft.fechaMayor);
        case FILTER_TYPES.MES:
            return (
                Number.isInteger(draft.mes) &&
                draft.mes >= 1 &&
                draft.mes <= 12 &&
                isYearInRange(draft.annoMes, now)
            );
        case FILTER_TYPES.ANNO:
            return isYearInRange(draft.anno, now);
        default:
            return false;
    }
}

export function applyFilterValidations(filters) {
    const today = moment().startOf('day');
    let { startDate, endDate, filterType } = filters;

    if (moment(endDate).isAfter(today)) {
        endDate = today.valueOf();
    }

    if (moment(startDate).isAfter(endDate)) {
        if (filterType === FILTER_TYPES.RANGO) {
            startDate = moment(endDate).subtract(1, 'month').valueOf();
        } else {
            startDate = moment(endDate).startOf('day').valueOf();
        }
    }

    const result = { ...filters, startDate, endDate };

    if (filterType === FILTER_TYPES.RANGO) {
        result.fechaDesde = startDate;
        result.fechaHasta = endDate;
    }

    if (filterType === FILTER_TYPES.MAYOR) {
        result.fechaMayor = startDate;
    }

    return result;
}

export function resolveFilters(rawFilters) {
    const normalized = normalizeLegacyFilters(rawFilters);
    const { startDate, endDate } = calcularFechas(normalized);
    return applyFilterValidations({ ...normalized, startDate, endDate });
}

export function parseYear(value, fallback) {
    if (value === '' || value == null) {
        return fallback;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

export function createDefaultFilters(now = moment()) {
    const today = now.clone().startOf('day');
    const monthAgo = today.clone().subtract(1, 'month');

    return {
        filterType: FILTER_TYPES.RANGO,
        startDate: monthAgo.valueOf(),
        endDate: today.valueOf(),
        fechaDesde: monthAgo.valueOf(),
        fechaHasta: today.valueOf(),
        fechaMayor: today.valueOf(),
        mes: today.month() + 1,
        annoMes: today.year(),
        anno: today.year(),
    };
}

export function formatFilterSummary(filters) {
    const { filterType } = filters;

    switch (filterType) {
        case FILTER_TYPES.RANGO:
            return `Rango: ${moment(filters.startDate).format('DD/MM/YYYY')} - ${moment(filters.endDate).format('DD/MM/YYYY')}`;
        case FILTER_TYPES.MAYOR:
            return `Mayor a: ${moment(filters.fechaMayor).format('DD/MM/YYYY')}`;
        case FILTER_TYPES.MES:
            return `Mes y Año: ${filters.mes}/${filters.annoMes}`;
        case FILTER_TYPES.ANNO:
            return `Año: ${filters.anno}`;
        default:
            return '';
    }
}
